import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createBusiness,
  placesAutocomplete,
  placesDetails,
  type CreateBusinessPayload,
} from "../lib/api.ts";

// Places autofill is enabled iff the gateway has GOOGLE_PLACES_KEY set. We
// detect that lazily — the first autocomplete call returns 500 "Places API
// not configured" and we hide the search box from then on. No API key
// shipped to the browser.
const PLACES_PROXY_ENABLED = (import.meta.env.VITE_PLACES_PROXY_ENABLED ?? "true") !== "false";

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 64)
    .replace(/^-|-$/g, "");
}

function detectTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
}

export default function CreateBusiness() {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [address1, setAddress1] = useState("");
  const [address2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [pincode, setPincode] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [placeId, setPlaceId] = useState("");
  const [latitude, setLatitude] = useState<number | undefined>(undefined);
  const [longitude, setLongitude] = useState<number | undefined>(undefined);
  const [timezone, setTimezone] = useState(detectTimeZone());

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-derive slug from name until the user manually edits it.
  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name));
  }, [name, slugTouched]);

  // ── Google Places (server-side proxy) ───────────────────────────────────────
  // Calls api.zedule.app/api/dashboard/places/{autocomplete,details}. The
  // Places API key lives only as a Worker secret on the gateway — never
  // shipped to the browser, never visible in DevTools.
  interface Prediction {
    placeId: string;
    text: string;
    secondary?: string;
  }

  const [placesQuery, setPlacesQuery] = useState("");
  const [placesPredictions, setPlacesPredictions] = useState<Prediction[]>([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [placesError, setPlacesError] = useState<string | null>(null);
  const [placesAvailable, setPlacesAvailable] = useState(PLACES_PROXY_ENABLED);
  // Session token: random UUID generated on first keystroke, reused for
  // every autocomplete call until the user picks a place. Google bills one
  // session = N autocompletes + 1 details, so this dedupes our cost.
  const [sessionToken, setSessionToken] = useState<string>("");
  // Set to true right before we programmatically update the query after a
  // selection so the autocomplete effect skips exactly one re-fetch.
  const skipNextLookup = useRef(false);

  const placesEnabled = placesAvailable;

  useEffect(() => {
    if (skipNextLookup.current) {
      skipNextLookup.current = false;
      setPlacesPredictions([]);
      return;
    }
    if (!placesEnabled || !placesQuery || placesQuery.length < 3) {
      setPlacesPredictions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setPlacesLoading(true);
        setPlacesError(null);
        let token = sessionToken;
        if (!token) {
          token = crypto.randomUUID();
          setSessionToken(token);
        }
        const { suggestions } = await placesAutocomplete(placesQuery, token);
        if (cancelled) return;
        setPlacesPredictions(
          suggestions.slice(0, 5).map((s) => ({
            placeId: s.placeId,
            text: s.mainText,
            secondary: s.secondaryText,
          })),
        );
      } catch (err) {
        const raw = err instanceof Error ? err.message : String(err);
        const message = raw
          .replace(/key=[A-Za-z0-9_-]+/g, "key=***")
          .replace(/AIza[A-Za-z0-9_-]{20,}/g, "***");
        console.warn("Places autocomplete failed:", err);
        if (!cancelled) {
          // If the gateway says the proxy isn't configured, hide the input
          // entirely — no point asking the user to type into something dead.
          if (/not configured/i.test(message)) {
            setPlacesAvailable(false);
          } else {
            setPlacesError(message);
          }
        }
      } finally {
        if (!cancelled) setPlacesLoading(false);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [placesQuery, placesEnabled, sessionToken]);

  const fillFromPlaceId = async (id: string) => {
    if (!placesEnabled) return;
    try {
      const place = await placesDetails(id, sessionToken);

      const pick = (type: string) =>
        place.addressComponents.find((c) => c.types.includes(type))?.longText ?? "";

      setName(place.displayName);
      setSlug(slugify(place.displayName));
      setSlugTouched(false);
      setAddress1(place.formattedAddress);
      setCity(pick("locality") || pick("postal_town") || pick("sublocality_level_1"));
      setState(pick("administrative_area_level_1"));
      setCountry(pick("country"));
      setPincode(pick("postal_code"));
      setPhone(place.internationalPhoneNumber || place.nationalPhoneNumber || "");
      setWebsite(place.websiteUri);
      setPlaceId(place.id || id);
      setLatitude(typeof place.location?.latitude === "number" ? place.location.latitude : undefined);
      setLongitude(typeof place.location?.longitude === "number" ? place.location.longitude : undefined);

      skipNextLookup.current = true;
      setPlacesPredictions([]);
      setPlacesQuery(place.displayName);
      // Session ends after a details fetch — clear so the next search starts a fresh one.
      setSessionToken("");
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      const message = raw
        .replace(/key=[A-Za-z0-9_-]+/g, "key=***")
        .replace(/AIza[A-Za-z0-9_-]{20,}/g, "***");
      console.warn("Places details fetch failed:", err);
      setPlacesError(message);
    }
  };

  const slugInvalid = useMemo(() => slug.length > 0 && !/^[a-z0-9](?:[a-z0-9-]{1,62}[a-z0-9])?$/.test(slug), [slug]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !slug || slugInvalid) return;
    setSubmitting(true);
    setError(null);
    const payload: CreateBusinessPayload = {
      name: name.trim(),
      slug,
      address_line1: address1 || undefined,
      address_line2: address2 || undefined,
      city: city || undefined,
      state: state || undefined,
      country: country || undefined,
      pincode: pincode || undefined,
      phone: phone || undefined,
      email: email || undefined,
      website_url: website || undefined,
      google_place_id: placeId || undefined,
      latitude,
      longitude,
      timezone,
    };
    try {
      const result = await createBusiness(payload);
      navigate(`/onboarding/${result.slug}`, { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--canvas)" }}>
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "56px 24px 80px" }}>
        <div
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 10.5,
            fontWeight: 500,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--ink-faint)",
            marginBottom: 12,
          }}
        >
          New workspace · 45-day trial
        </div>
        <h1
          style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontWeight: 500,
            fontSize: 36,
            letterSpacing: "-0.018em",
            color: "var(--ink)",
            margin: "0 0 8px",
          }}
        >
          Create your business.
        </h1>
        <p style={{ color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.55, margin: "0 0 32px", maxWidth: "60ch" }}>
          Forty-five days free. No card. {placesEnabled ? "Search Google for your business to autofill, or fill in manually below." : "Fill in the details — the only required fields are the name and a URL slug."}
        </p>

        {placesEnabled && (
          <div style={{ marginBottom: 28, position: "relative" }}>
            <label className="label" htmlFor="places-input">
              Find your business on Google
            </label>
            <input
              id="places-input"
              className="input"
              type="text"
              autoComplete="off"
              placeholder="Start typing your business name…"
              value={placesQuery}
              onChange={(e) => setPlacesQuery(e.target.value)}
              style={{ width: "100%" }}
            />
            {placesPredictions.length > 0 && (
              <ul
                style={{
                  position: "absolute",
                  zIndex: 10,
                  top: "100%",
                  left: 0,
                  right: 0,
                  marginTop: 4,
                  padding: 6,
                  listStyle: "none",
                  background: "var(--paper-raised)",
                  border: "1px solid var(--rule)",
                  borderRadius: 10,
                  boxShadow: "var(--shadow-md)",
                }}
              >
                {placesPredictions.map((p) => (
                  <li key={p.placeId}>
                    <button
                      type="button"
                      onClick={() => fillFromPlaceId(p.placeId)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "8px 10px",
                        background: "transparent",
                        border: "none",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontSize: 13.5,
                        color: "var(--ink)",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--paper-well)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <span style={{ fontWeight: 500 }}>{p.text}</span>
                      {p.secondary && (
                        <span style={{ display: "block", fontSize: 12, color: "var(--ink-faint)", marginTop: 2 }}>
                          {p.secondary}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p style={{ fontSize: 12, color: "var(--ink-faint)", marginTop: 6 }}>
              {placesLoading ? "Searching…" : "Powered by Google Places. Optional — skip and type manually below."}
            </p>
            {placesError && (
              <div
                style={{
                  marginTop: 8,
                  padding: "8px 10px",
                  background: "var(--status-cancelled-bg)",
                  border: "1px solid var(--status-cancelled)",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "var(--ink)",
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  wordBreak: "break-word",
                }}
              >
                <strong>Places API error:</strong> {placesError}
              </div>
            )}
          </div>
        )}

        <form onSubmit={submit} className="space-y-5" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div>
            <label className="label" htmlFor="b-name">Business name *</label>
            <input id="b-name" className="input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Riverstone Family Chiropractic" />
          </div>

          <div>
            <label className="label" htmlFor="b-slug">URL slug *</label>
            <div style={{ display: "flex", alignItems: "stretch", gap: 0 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "0 10px",
                  fontSize: 13,
                  color: "var(--ink-faint)",
                  background: "var(--paper-well)",
                  border: "1px solid var(--rule)",
                  borderRight: "none",
                  borderRadius: "8px 0 0 8px",
                }}
              >
                book.zedule.app/
              </span>
              <input
                id="b-slug"
                className="input"
                required
                value={slug}
                onChange={(e) => { setSlug(e.target.value.toLowerCase()); setSlugTouched(true); }}
                placeholder="riverstone-chiro"
                style={{ borderRadius: "0 8px 8px 0", flex: 1 }}
              />
            </div>
            {slugInvalid && (
              <p style={{ fontSize: 12, color: "var(--status-cancelled)", marginTop: 4 }}>
                Use lowercase letters, numbers, and hyphens. 3–64 chars, no leading or trailing hyphen.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="b-phone">Phone</label>
              <input id="b-phone" className="input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 (555) 555-0100" />
            </div>
            <div>
              <label className="label" htmlFor="b-email">Contact email</label>
              <input id="b-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="hello@example.com" />
            </div>
          </div>

          <div>
            <label className="label" htmlFor="b-addr1">Street address</label>
            <input id="b-addr1" className="input" value={address1} onChange={(e) => setAddress1(e.target.value)} placeholder="128 River Road" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="label" htmlFor="b-city">City</label>
              <input id="b-city" className="input" value={city} onChange={(e) => setCity(e.target.value)} />
            </div>
            <div>
              <label className="label" htmlFor="b-state">State / Region</label>
              <input id="b-state" className="input" value={state} onChange={(e) => setState(e.target.value)} />
            </div>
            <div>
              <label className="label" htmlFor="b-pincode">Postal</label>
              <input id="b-pincode" className="input" value={pincode} onChange={(e) => setPincode(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="b-country">Country</label>
              <input id="b-country" className="input" value={country} onChange={(e) => setCountry(e.target.value)} />
            </div>
            <div>
              <label className="label" htmlFor="b-tz">Time zone</label>
              <input id="b-tz" className="input" value={timezone} onChange={(e) => setTimezone(e.target.value)} placeholder="America/New_York" />
            </div>
          </div>

          <div>
            <label className="label" htmlFor="b-website">Website</label>
            <input id="b-website" className="input" type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://example.com" />
          </div>

          {error && (
            <div className="alert-error" style={{ fontSize: 13.5 }}>{error}</div>
          )}

          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <button
              type="submit"
              className="zd-hero-cta"
              disabled={submitting || !name || !slug || slugInvalid}
              style={{ opacity: submitting || !name || !slug || slugInvalid ? 0.4 : 1 }}
            >
              {submitting ? "Creating…" : "Create business →"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              style={{
                background: "transparent",
                border: "1px solid var(--rule)",
                borderRadius: 10,
                padding: "0 18px",
                height: 44,
                fontSize: 13.5,
                color: "var(--ink-soft)",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
